import {
  AssetClassCode,
  AssetSubClassCode,
  Class,
} from '@api/api/entity-constants';
import { Entity, PrimaryMethod } from '@api/api/entity-schema';
import {
  DisplayFormat,
  MethodSubClass,
  SnakeMeta,
} from '@api/api/timeseries-schema';
import { mapSnakeSeriesToOhlc } from '@helpers/utils/asset-price/asset-price';
import { isSinglePriceEntity } from '@helpers/utils/entity/entity';

export const THEME_CHART_TAG_PREFIX = 'theme:';
export const THEME_PERFORMANCE_SNAKE_SUFFIX = 'theme_performance';
export const THEMES_SUBCLASS_CHIP = 'themes';

const EMPTY_LOGO_URLS = {
  dark_svg: '',
  light_svg: '',
  dark_png: '',
  light_png: '',
};

export interface ThemeChartIdentity {
  id: string;
  name: string;
  logo_url?: string;
}

export const toThemeChartTag = (themeId: string) =>
  `${THEME_CHART_TAG_PREFIX}${themeId}`;

export const isThemeChartTag = (tag: string) =>
  tag.startsWith(THEME_CHART_TAG_PREFIX);

export const parseThemeIdFromChartTag = (tag: string) =>
  tag.slice(THEME_CHART_TAG_PREFIX.length);

export const getThemePerformanceSnake = (themeId: string) =>
  `${themeId}.${THEME_PERFORMANCE_SNAKE_SUFFIX}`;

export const isThemePerformanceSnake = (snake: string) =>
  snake.endsWith(`.${THEME_PERFORMANCE_SNAKE_SUFFIX}`);

export const parseThemeIdFromPerformanceSnake = (snake: string) =>
  snake.slice(0, snake.length - `.${THEME_PERFORMANCE_SNAKE_SUFFIX}`.length);

export const isThemeChartEntity = (entity: Pick<Entity, 'tag'>) =>
  isThemeChartTag(entity.tag);

export const isChartSinglePriceEntity = (entity: Entity): boolean =>
  !isThemeChartEntity(entity) && isSinglePriceEntity(entity);

const INTRADAY_THEME_RESAMPLES = ['1m', '5m', '15m', '30m', '1h'] as const;

export const getThemeHorizonForResolution = (
  resolution: string
): 'intraday' | '1y' =>
  (INTRADAY_THEME_RESAMPLES as readonly string[]).includes(resolution)
    ? 'intraday'
    : '1y';

export const mapThemeTimeseriesToChartData = (
  points: Array<{ index: string; value: number }>,
  keepIntradayTimestamps: boolean
) =>
  mapSnakeSeriesToOhlc({
    data: points.map(point => ({
      index: point.index,
      value: point.value,
    })),
    isPrice: keepIntradayTimestamps,
  });

export const createThemeChartEntity = ({
  id,
  name,
  logo_url,
}: ThemeChartIdentity): Entity => {
  const logoUrls = logo_url
    ? {
        dark_svg: logo_url,
        light_svg: logo_url,
        dark_png: logo_url,
        light_png: logo_url,
      }
    : EMPTY_LOGO_URLS;

  return {
    tag: toThemeChartTag(id),
    id,
    active: true,
    asset_class: AssetClassCode.Equity,
    class: Class.ClassStock,
    currency: '',
    default_snake: getThemePerformanceSnake(id),
    exchange: { code: '', name: '' },
    gics: {
      industry_group: { code: '' },
      sector: { code: '', name: '' },
    },
    primary_method: PrimaryMethod.Price,
    search: { rank: 0 },
    sub_class: AssetSubClassCode.EquityStock,
    ticker: name,
    subscribable_ticker: toThemeChartTag(id),
    logo_url: logo_url ?? '',
    logo_urls: logoUrls,
    gated: false,
    name,
    name_full: name,
    name_short: name,
  };
};

export const createThemeChartSnakeMeta = ({
  id,
  name,
}: ThemeChartIdentity): SnakeMeta => ({
  active: true,
  alified_last_timestamp: new Date(0).toISOString(),
  before_last_timestamp: 0,
  before_last_value: 0,
  construct_scenarios: false,
  currency: '',
  data_entitlement: '',
  data_source: '',
  display_format: DisplayFormat.PERCENT,
  entity: toThemeChartTag(id),
  field: THEME_PERFORMANCE_SNAKE_SUFFIX,
  gated: false,
  is_percentage: true,
  last_timestamp: 0,
  last_value: 0,
  method_sub_class: MethodSubClass.Price,
  method_sub_class_group: 0,
  method_sub_class_name: '',
  method_sub_class_sub_group: 0,
  name: {
    english: name,
    korean: '',
    japanese: '',
    kanji: '',
    chinese: '',
  },
  search: { rank: 0, keywords: [] },
  snake: getThemePerformanceSnake(id),
  stationary: false,
});
